import { getBrandResource, isVideoFile } from "@utils/misc";
import { cn } from "kz-ui-sdk";
import { DetailedHTMLProps, ImgHTMLAttributes, VideoHTMLAttributes, useEffect, useRef, useState } from "react";

export interface MediaWithFallbackProps
  extends DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
  wrapperClassName?: string;
}

type FallbackTextProps = {
  text: string;
};

const FallBackText = ({ text }: FallbackTextProps) => (
  <div className={cn("flex h-full w-full items-center justify-center p-1 text-center")}>
    <span className="line-clamp-3 overflow-hidden text-xs">{text}</span>
  </div>
);

const MediaWithFallback = ({ src, alt, className, wrapperClassName, ...props }: MediaWithFallbackProps) => {
  const [isError, setIsError] = useState(false);
  const [isLogoError, setIsLogoError] = useState(false);
  const [isLoading, setIsLoading] = useState(() => !!src);
  const imgRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reset states when src changes and check if already loaded from cache
  useEffect(() => {
    if (src) {
      setIsError(false);
      setIsLogoError(false);

      // Check if image/video is already loaded from cache
      const img = imgRef.current;
      const video = videoRef.current;

      if (img?.complete && img.naturalHeight !== 0) {
        setIsLoading(false);
      } else if (video && video.readyState >= 3) {
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }
    } else {
      setIsLoading(false);
      setIsError(false);
      setIsLogoError(false);
    }
  }, [src]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement, Event>) => {
    setIsError(true);
    setIsLoading(false);
    // Call original onError handler if provided
    if (props.onError) {
      props.onError(e as React.SyntheticEvent<HTMLImageElement, Event>);
    }
  };

  const handleLogoError = () => {
    setIsLogoError(true);
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  const renderPlaceholder = () => (
    <div className={cn("absolute start-0 top-0 h-full w-full p-2", wrapperClassName)}>
      {isLogoError ? (
        <FallBackText text={alt || "game icon"} />
      ) : (
        <img
          src={getBrandResource("logo.png")}
          alt={alt || "game icon"}
          className={cn("h-full w-full object-contain opacity-10 grayscale", className)}
          onError={handleLogoError}
        />
      )}
    </div>
  );

  if (!src || isError) {
    return <FallBackText text={alt || "game icon"} />;
  }

  // If the source is a webm file, render a video element
  if (isVideoFile(src)) {
    const handleVideoLoad = () => {
      handleLoad();
    };

    const videoProps: DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement> = {
      src,
      ref: videoRef,
      onError: handleError as (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void,
      onLoadedData: handleVideoLoad,
      onCanPlay: handleVideoLoad,
      autoPlay: true,
      muted: true,
      loop: true,
      playsInline: true,
      className: cn(
        "absolute start-0 top-0 h-full w-full object-fill transition-opacity",
        isLoading ? "opacity-0" : "opacity-100",
        className,
      ),
    };

    return (
      <div className={cn("relative h-full w-full p-1", wrapperClassName)}>
        {isLoading && renderPlaceholder()}
        <video {...videoProps} />
      </div>
    );
  }

  // Image case: use the img element
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    handleLoad();
    // Call original onLoad handler if provided
    if (props.onLoad) {
      props.onLoad(e);
    }
  };

  return (
    <div className={cn("relative h-full w-full p-1", wrapperClassName)}>
      {isLoading && renderPlaceholder()}
      <img
        {...props}
        ref={imgRef}
        src={src}
        alt={alt}
        onError={handleError}
        onLoad={handleImageLoad}
        className={cn(
          "absolute start-0 top-0 h-full w-full object-fill transition-opacity",
          isLoading ? "opacity-0" : "opacity-100",
          className,
        )}
      />
    </div>
  );
};

export default MediaWithFallback;
